/**
 * UpSurv 2.0 — QR onboarding, the pure half (DECISIONS D-013).
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * The A2P 10DLC campaign was denied (D-010), so outbound SMS is parked and a
 * brand-new surveyor has no channel on which to receive their first token. The
 * QR is that channel. Stephanie provisions in person: the admin page prints a
 * one-time link as a scannable code, the surveyor's phone camera opens it, and
 * the app redeems it for a real access token.
 *
 * D-005 still holds. "Chip and Tim never handle tokens" is a statement about
 * credentials passing through human hands — being read out, typed, forwarded,
 * copied into a chat. A code displayed on an office screen to be scanned by the
 * phone standing in front of it is a DELIVERY CHANNEL, exactly as the SMS was.
 * Nothing on the admin page renders a token as text, and nothing there accepts
 * one as input.
 *
 * ── What is in this file, and what is deliberately not ──────────────────────
 * Everything here is pure and runs in both a Worker and a browser: the link
 * format, the 24-hour window, and the shape check. The STORAGE rules
 * (hash-only, atomic single-use consumption) live in src/db/queries.ts and in
 * the schema, because that is where they can actually be enforced.
 *
 * The secret's alphabet and length are `generateAccessToken`'s, reused rather
 * than reinvented: 256 bits from the CSPRNG, base64url, no padding — and the
 * same `looksLikeAccessToken` pre-filter keeps junk out of a SHA-256 and a D1
 * round trip. What makes it a DIFFERENT credential is where it is stored
 * (`onboarding_links`, not `surveyors`) and the URL parameter it arrives on.
 */

import { looksLikeAccessToken } from './tokens';

/* ──────────────────────────────── the window ─────────────────────────────── */

/** The design authority's number: a code is good for one day, then it is dead. */
export const ONBOARDING_TTL_HOURS = 24;

export const ONBOARDING_TTL_MS = ONBOARDING_TTL_HOURS * 60 * 60 * 1000;

/**
 * `created_at + 24h`, from an instant the SERVER produced.
 *
 * Never call this with a client-supplied timestamp. The whole point of a
 * server-side expiry is that a phone with a wrong clock — or a helpful one —
 * cannot widen its own window.
 */
export function onboardingExpiry(issuedAtIso: string): string {
  const issued = Date.parse(issuedAtIso);
  if (Number.isNaN(issued)) {
    throw new Error(`onboardingExpiry: "${issuedAtIso}" is not an instant`);
  }
  return new Date(issued + ONBOARDING_TTL_MS).toISOString();
}

/**
 * Has this window closed, judged against an instant the caller vouches for?
 *
 * The API does NOT use this to decide a redemption — that decision is made by
 * SQL, inside the single guarded UPDATE that consumes the row, because a
 * separate read would be a second decision free to disagree with the write.
 * This is for reporting: telling the surveyor *why* a code was refused, and
 * telling the office how long the one on their screen has left.
 */
export function onboardingExpired(expiresAtIso: string, nowIso: string): boolean {
  const expires = Date.parse(expiresAtIso);
  const now = Date.parse(nowIso);
  if (Number.isNaN(expires) || Number.isNaN(now)) return true;
  return now >= expires;
}

/* ───────────────────────────────── the link ──────────────────────────────── */

/**
 * The URL parameter the secret rides on.
 *
 * NOT `t`. An access token and an onboarding secret are different credentials
 * with different lifetimes redeemed at different endpoints, and sharing a
 * parameter name would mean the device had to guess which it was holding —
 * storing an onboarding secret as if it were a token would leave a phone
 * permanently unauthenticated with no way to say why.
 */
export const ONBOARDING_PARAM = 'o';

/**
 * The URL the QR encodes.
 *
 * In the FRAGMENT, like the assignment link before it, and for the same reason:
 * a fragment is never sent to the origin, so the secret cannot land in an
 * access log, a proxy log or a referrer header. `claimOnboardingFromLocation()`
 * takes it out of the address bar with `replaceState` before any screen
 * renders, so it is not in history either.
 */
export function onboardingLink(appBaseUrl: string, secret: string): string {
  return `${appBaseUrl.replace(/\/+$/, '')}/#${ONBOARDING_PARAM}=${secret}`;
}

/**
 * Shape check only. Redemption is decided by a hash lookup inside the consuming
 * UPDATE, never by this — its job is to reject obvious junk before it costs a
 * SHA-256 and a D1 round trip.
 */
export function looksLikeOnboardingSecret(candidate: string): boolean {
  return looksLikeAccessToken(candidate);
}

/* ─────────────────────────────── why it failed ───────────────────────────── */

/**
 * Every way a redemption can be refused.
 *
 * These are told apart in the response ON PURPOSE, and the reasoning is worth
 * writing down because the instinct in auth code is the opposite. An access
 * token gets one indistinguishable `unauthorized` for every failure, so that
 * validity never leaks. An onboarding secret is 256 bits held for 24 hours and
 * redeemable once; nobody is probing it, and the person holding the phone is
 * standing next to the person who printed it. What they need is the difference
 * between "this was already scanned" and "this expired — print another", which
 * are different actions. Collapsing them would make the one screen that exists
 * to unblock a stuck surveyor say nothing useful.
 */
export type OnboardingRefusal =
  /** No link has that secret. A mistyped or truncated scan lands here too. */
  | 'unknown'
  /** Already redeemed — the defining property of the feature, working. */
  | 'used'
  /** Past its 24 hours, judged against server time. */
  | 'expired'
  /** A newer code was printed for this surveyor, which killed this one. */
  | 'replaced'
  /** The surveyor was deactivated after the code was printed. */
  | 'surveyor_inactive';
